import { useEffect, useRef, useState } from "react";
import { Search, Phone, MapPin, Star, MousePointerClick, PhoneCall } from "lucide-react";

const QUERY = "friseur termin heute münchen";

const STEPS = [
  { label: "Suche", hint: "Kundin sucht spontan einen Termin" },
  { label: "Anzeige", hint: "Deine Anzeige erscheint ganz oben" },
  { label: "Salon-Seite", hint: "Ein Klick führt auf deine Salon-Seite" },
  { label: "Anruf", hint: "Sie ruft an – du vergibst den Termin" },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export function BoostStorySequence() {
  const reduced = usePrefersReducedMotion();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setVisible(true);
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Timeline
  useEffect(() => {
    if (!visible || reduced) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const run = () => {
      if (cancelled) return;
      setStep(0);
      setTyped(0);
      // typing
      for (let i = 1; i <= QUERY.length; i++) {
        timers.push(setTimeout(() => setTyped(i), i * 55));
      }
      const typingEnd = QUERY.length * 55 + 350;
      timers.push(setTimeout(() => setStep(1), typingEnd));
      timers.push(setTimeout(() => setStep(2), typingEnd + 1400));
      timers.push(setTimeout(() => setStep(3), typingEnd + 2900));
      timers.push(setTimeout(run, typingEnd + 6200));
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [visible, reduced]);

  const done = reduced;
  const activeStep = done ? 3 : step;
  const typedText = done ? QUERY : QUERY.slice(0, typed);
  const showAd = done || step >= 1;
  const showPointer = !done && step === 1;
  const showLanding = done || step >= 2;
  const calling = done || step >= 3;

  return (
    <div ref={containerRef} className="space-y-8">
      <div className="grid items-start gap-6 md:grid-cols-2">
        {/* Google-Suche */}
        <div className="space-y-3">
          <div className="rounded-2xl border bg-card p-4 shadow-soft sm:p-5">
            <div className="flex items-center gap-2 rounded-full border bg-background px-3 py-2 text-xs text-muted-foreground">
              <Search className="size-3.5 shrink-0" />
              <span className="truncate">
                {typedText}
                {!done && typed < QUERY.length ? (
                  <span className="ml-0.5 inline-block h-3 w-px animate-pulse bg-foreground align-middle" />
                ) : null}
              </span>
            </div>

            <div
              className={`relative mt-4 transition-all duration-500 ${
                showAd ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
              }`}
            >
              <div
                className={`rounded-xl border p-3 transition-colors duration-300 ${
                  showPointer ? "border-primary bg-primary/5" : "border-transparent"
                }`}
              >
                <p className="text-[10px] text-muted-foreground">
                  Anzeige ·{" "}
                  <span className="text-foreground">salonturbo.de/s/frisurenstudio-mueller</span>
                </p>
                <p className="mt-1 font-display text-base font-medium leading-tight text-primary">
                  Frisurenstudio Müller – Heute noch Termine frei
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Schnitt, Farbe &amp; Styling in München-Laim. Ruf uns an – wir finden dir einen
                  Termin.
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-2 text-xs">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Phone className="size-3" /> Anrufen
                  </span>
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <MapPin className="size-3" /> 300 m entfernt
                  </span>
                  <span className="inline-flex items-center gap-1 text-yellow-600">
                    <Star className="size-3 fill-current" /> 4,9
                  </span>
                </div>
              </div>
              <MousePointerClick
                className={`absolute left-1/2 top-8 size-5 text-primary transition-all duration-300 ${
                  showPointer ? "scale-100 opacity-100" : "scale-75 opacity-0"
                }`}
              />
            </div>

            <div className="mt-3 space-y-2 opacity-40">
              <div className="h-2 w-2/3 rounded-full bg-muted" />
              <div className="h-2 w-1/2 rounded-full bg-muted" />
            </div>
          </div>
          <p className="px-1 text-center text-sm text-muted-foreground">
            Jemand in deiner Stadt sucht jetzt einen Termin – und sieht deinen Salon.
          </p>
        </div>

        {/* Landingpage */}
        <div className="space-y-3">
          <div
            className={`overflow-hidden rounded-2xl border bg-card shadow-soft transition-all duration-500 ${
              showLanding ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
            }`}
          >
            <div className="h-20 bg-secondary">
              <div className="flex h-full items-end justify-between px-4 pb-3">
                <span className="wordmark text-[10px]">SalonTurbo</span>
                <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] text-primary-foreground">
                  Heute frei
                </span>
              </div>
            </div>
            <div className="space-y-3 p-4 text-center">
              <p className="font-display text-lg font-medium">Frisurenstudio Müller</p>
              <p className="text-xs text-muted-foreground">Friseur · München-Laim</p>
              <div
                className={`flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-xs font-semibold text-primary-foreground transition-transform duration-300 ${
                  calling ? "scale-[1.03]" : "scale-100"
                }`}
              >
                {calling ? <PhoneCall className="size-4" /> : <Phone className="size-4" />}
                {calling ? "Anruf läuft …" : "Jetzt anrufen"}
              </div>
              <div
                className={`transition-all duration-500 ${
                  calling ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p className="rounded-xl bg-success/10 px-3 py-2 text-xs font-medium text-success">
                  Termin vergeben – heute 16:30 Uhr
                </p>
              </div>
              <p className="text-[10px] text-muted-foreground">Öffnungszeiten · Route · Galerie</p>
            </div>
          </div>
          <p className="px-1 text-center text-sm text-muted-foreground">
            Ein Klick, ein Anruf – der Stuhl von heute Nachmittag ist besetzt.
          </p>
        </div>
      </div>

      {/* Fortschritt */}
      <div className="grid gap-3 sm:grid-cols-4">
        {STEPS.map((s, i) => {
          const isActive = activeStep >= i;
          return (
            <div key={s.label} className="space-y-2">
              <div
                className={`h-1 rounded-full transition-colors duration-500 ${
                  isActive ? "bg-primary" : "bg-border"
                }`}
              />
              <p
                className={`text-xs font-medium transition-colors duration-500 ${
                  isActive ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {i + 1}. {s.label}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">{s.hint}</p>
            </div>
          );
        })}
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Vom Suchen bis zum Anruf liegen oft nur Minuten – alles am gleichen Tag.
      </p>
    </div>
  );
}
